/**
 * Quantos chamados o servidor diz que uma COLUNA de uma faixa do quadro tem.
 *
 * É o número depois da barra no cabeçalho da coluna ("2/5" = 2 carregados, 5
 * no total). O número antes da barra é o que a tela tem; este é o que o
 * agregado (`GET /ticket/assignee-counts`) conta.
 *
 * ## O defeito que isto conserta
 *
 * As colunas do kanban são definidas por status E SUBSTATUS. O
 * "Kanban - Comercial" tem uma coluna "status 3 com substatus 3, 5, 6…" e
 * outra "status 3 SEM substatus". Contando só por status, o mesmo chamado era
 * somado nas duas, e a segunda mostrava "0/1": o cabeçalho prometendo um
 * cartão que a coluna, corretamente, não tem.
 *
 * Medido em produção em 23/10/2026, na faixa Comercial, logo depois de
 * consertar outro defeito com o MESMO sintoma -- coluna anunciando o que não
 * mostra. Os dois eram causas diferentes, e foi por isso que o segundo ficou
 * escondido atrás do primeiro.
 *
 * ## O degrau para o servidor antigo
 *
 * `by_status_substatus` é novo. A api e a tela são deployadas separadamente, e
 * existe uma janela em que a tela nova fala com a api velha: sem o degrau, o
 * total viria ZERO em todas as colunas, o kanban não pediria nenhuma página e
 * o quadro inteiro apareceria vazio. Somar por status é o comportamento
 * antigo, errado só nas colunas com substatus.
 */

/** A chave que o servidor usa: `status:substatus`, com substatus nulo vazio. */
export const chaveDaColuna = (statusId, substatusId) =>
    `${statusId}:${substatusId == null ? '' : substatusId}`;

/**
 * @param {object|null} grupo  a linha do agregado para esta faixa
 * @param {object} coluna      a coluna do filtro, com `items`
 * @returns {number}
 */
export const totalDaColunaDoKanban = (grupo, coluna) => {
    const itens = Array.isArray(coluna?.items) ? coluna.items : [];
    if (itens.length === 0) return 0;

    const porStatusESub = grupo?.by_status_substatus;

    if (porStatusESub && typeof porStatusESub === 'object') {
        /*
         * Pares repetidos contam UMA vez. Um filtro pode listar o mesmo
         * status+substatus duas vezes na mesma coluna -- é só configuração
         * torta, não um chamado a mais --, e somar duas vezes daria um total
         * que a coluna nunca alcança.
         */
        const vistos = new Set();

        return itens.reduce((soma, it) => {
            const chave = chaveDaColuna(it?.status_id, it?.substatus_id);
            if (vistos.has(chave)) return soma;
            vistos.add(chave);

            return soma + (Number(porStatusESub[chave]) || 0);
        }, 0);
    }

    /*
     * Servidor antigo. Os status repetidos contam UMA vez: uma coluna lista o
     * mesmo status várias vezes quando se define por substatus, e sem a
     * deduplicação o total era multiplicado pelo número de itens -- "4315 x 2
     * = 8630" no cabeçalho, que foi como este caso apareceu da primeira vez.
     *
     * Este degrau erra nas colunas com substatus; é o preço de falar com uma
     * api que não separa por substatus, e é por isso que ele é só o degrau.
     */
    const porStatus = grupo?.by_status || {};
    const statusVistos = [...new Set(itens.map((it) => String(it?.status_id)))];

    return statusVistos.reduce((soma, sid) => soma + (Number(porStatus[sid]) || 0), 0);
};
